<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { subDays, fromUnixTime } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { useRoute, useRouter } from 'vue-router';
import {
  parseReportURLParams,
  parseFilterURLParams,
  generateCompleteURLParams,
} from 'dashboard/routes/dashboard/settings/reports/helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  buildFilterList,
  getActiveFilter,
} from 'dashboard/routes/dashboard/settings/reports/components/Csat/CsatFilterHelpers';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import FilterButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import ActiveFilterChip from 'dashboard/routes/dashboard/settings/reports/components/Filters/v3/ActiveFilterChip.vue';
import AddFilterChip from 'dashboard/routes/dashboard/settings/reports/components/Filters/v3/AddFilterChip.vue';
import CsatMetricCard from 'dashboard/routes/dashboard/settings/reports/components/CsatMetricCard.vue';
import ReportHeader from 'dashboard/routes/dashboard/settings/reports/components/ReportHeader.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import lossReportAPI from 'kanban/api/lossReport';
import kanbanModule from 'kanban/store/modules/kanban';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

if (!store.hasModule('kanban')) {
  store.registerModule('kanban', kanbanModule);
}

// Gating do Kanban (decisão #16) — relatório de Perdas x Agentes é um relatório do Kanban.
const { isCloudFeatureEnabled, accountId, isOnChatwootCloud } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const escaleshopSubscription = computed(
  () => store.getters['globalConfig/getEscaleshopSubscription']
);
const isEscaleshopSubscriptionActive = computed(() =>
  ['active', 'past_due', 'trialing'].includes(
    escaleshopSubscription.value?.status
  )
);
const isKanbanEnabled = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.KANBAN) &&
    isEscaleshopSubscriptionActive.value
);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);
const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

// --- URL params init ---
const urlParams = parseReportURLParams(route.query);
const urlFilters = parseFilterURLParams(route.query);
const urlBoardId = route.query.board_id ? String(route.query.board_id) : null;

const initialDateRange =
  urlParams.from && urlParams.to
    ? [fromUnixTime(urlParams.from), fromUnixTime(urlParams.to)]
    : [subDays(new Date(), 29), new Date()];

const customDateRange = ref(initialDateRange);
const selectedDateRange = ref(urlParams.range || DATE_RANGE_TYPES.LAST_30_DAYS);

// --- Selected board (required) ---
const selectedBoardId = ref(urlBoardId);

// --- Applied agent/team filters ---
const appliedFilters = ref({
  agent_id: urlFilters.agent_id || null,
  team_id: urlFilters.team_id || null,
});

// --- Filter dropdown state ---
const showDropdownMenu = ref(false);
const showSubDropdownMenu = ref(false);
const activeFilterType = ref('');

// --- Store data ---
const agents = computed(() => store.getters['agents/getAgents']);
const teams = computed(() => store.getters['teams/getTeams']);
const boards = computed(() => store.state.kanban.boards);

// --- Board selector ---
const boardOptions = computed(() =>
  boards.value.map(b => ({
    label: b.name,
    value: String(b.id),
  }))
);

const selectedBoardLabel = computed(() => {
  const board = boards.value.find(b => String(b.id) === selectedBoardId.value);
  return board?.name || t('KANBAN.SELECT_BOARD');
});

const boardCurrency = computed(() => {
  const board = boards.value.find(b => String(b.id) === selectedBoardId.value);
  return board?.currency || null;
});

// --- Dates ---
const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

// --- Filter chip helpers ---
const getFilterSource = type => {
  if (type === 'agents') return agents.value;
  if (type === 'teams') return teams.value;
  return [];
};

const getFilterOptions = type => buildFilterList(getFilterSource(type), type);

const filterKeyToType = { agent_id: 'agents', team_id: 'teams' };
const filterTypeToKey = { agents: 'agent_id', teams: 'team_id' };

const filterListMenuItems = computed(() => {
  const all = [
    { id: '1', name: t('CSAT_REPORTS.FILTERS.AGENTS.LABEL'), type: 'agents' },
    { id: '2', name: t('CSAT_REPORTS.FILTERS.TEAMS.LABEL'), type: 'teams' },
  ];
  const activeTypes = Object.keys(appliedFilters.value)
    .filter(k => appliedFilters.value[k])
    .map(k => filterKeyToType[k]);
  return all
    .filter(({ type }) => !activeTypes.includes(type))
    .map(({ id, name, type }) => ({
      id, name, type, options: getFilterOptions(type),
    }));
});

const activeFilters = computed(() =>
  Object.keys(appliedFilters.value)
    .filter(k => appliedFilters.value[k])
    .map(k => {
      const type = filterKeyToType[k];
      const items = getFilterSource(type);
      const item = getActiveFilter(items, type, appliedFilters.value[k]);
      return {
        id: item?.id || appliedFilters.value[k],
        name: item?.name || `ID: ${appliedFilters.value[k]}`,
        type,
        options: getFilterOptions(type),
      };
    })
);

const hasActiveFilters = computed(() =>
  Object.values(appliedFilters.value).some(v => v !== null)
);
const isAllFilterSelected = computed(() => !filterListMenuItems.value.length);

// --- Report data ---
const isLoading = ref(false);
const summary = ref({ total_losses: 0, total_value: 0 });
const rows = ref([]);

// --- URL sync ---
const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
    filters: {
      agent_id: appliedFilters.value.agent_id,
      team_id: appliedFilters.value.team_id,
    },
  });
  if (selectedBoardId.value) params.board_id = selectedBoardId.value;
  router.replace({ query: params });
};

// --- Fetch ---
const fetchReport = async () => {
  if (!selectedBoardId.value) return;
  updateURLParams();
  isLoading.value = true;
  try {
    const params = { since: from.value, until: to.value, board_id: selectedBoardId.value };
    if (appliedFilters.value.agent_id) params.agent_id = appliedFilters.value.agent_id;
    if (appliedFilters.value.team_id) params.team_id = appliedFilters.value.team_id;
    const { data } = await lossReportAPI.get(params);
    summary.value = data.summary;
    rows.value = data.data;
  } finally {
    isLoading.value = false;
  }
};

// --- Filter actions ---
const resetDropdown = () => {
  showDropdownMenu.value = false;
  showSubDropdownMenu.value = false;
  activeFilterType.value = '';
};

const addFilter = item => {
  const key = filterTypeToKey[item.type];
  appliedFilters.value[key] = item.id;
  fetchReport();
  resetDropdown();
};

const removeFilter = type => {
  const key = filterTypeToKey[type];
  appliedFilters.value[key] = null;
  fetchReport();
};

const clearAllFilters = () => {
  appliedFilters.value = { agent_id: null, team_id: null };
  fetchReport();
  resetDropdown();
};

const showDropdown = () => {
  showSubDropdownMenu.value = false;
  showDropdownMenu.value = !showDropdownMenu.value;
};

const openActiveFilterDropdown = filterType => {
  showDropdownMenu.value = false;
  activeFilterType.value = filterType;
  showSubDropdownMenu.value = !showSubDropdownMenu.value;
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchReport();
};

const onBoardChange = boardId => {
  selectedBoardId.value = boardId;
  fetchReport();
};

// --- Table grouping ---
const grouped = computed(() => {
  const groups = {};
  rows.value.forEach(row => {
    const teamKey = row.team_id ?? '__none__';
    if (!groups[teamKey]) {
      groups[teamKey] = { teamId: row.team_id, teamName: row.team_name, agents: {} };
    }
    const agentKey = row.agent_id ?? '__none__';
    if (!groups[teamKey].agents[agentKey]) {
      groups[teamKey].agents[agentKey] = {
        agentId: row.agent_id,
        agentName: row.agent_name,
        reasons: [],
      };
    }
    groups[teamKey].agents[agentKey].reasons.push({
      reasonId: row.loss_reason_id,
      reasonName: row.loss_reason_name,
      lossCount: row.loss_count,
      totalValue: row.total_value,
    });
  });
  return Object.values(groups).map(g => ({
    ...g,
    agents: Object.values(g.agents),
  }));
});

const formatCurrency = val => {
  const num = val || 0;
  if (boardCurrency.value) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: boardCurrency.value,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

onMounted(async () => {
  if (!isKanbanEnabled.value) return;
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  await store.dispatch('kanban/fetchBoards');
  if (!selectedBoardId.value && boards.value.length > 0) {
    selectedBoardId.value = String(boards.value[0].id);
  }
  fetchReport();
});
</script>

<template>
  <div
    v-if="!isKanbanEnabled"
    class="flex items-center justify-center h-full min-h-[400px] px-6 py-12"
  >
    <BasePaywallModal
      feature-prefix="KANBAN"
      :i18n-key="paywallI18nKey"
      :is-super-admin="isSuperAdmin"
      :is-on-chatwoot-cloud="isOnChatwootCloud"
      @upgrade="openBilling"
    />
  </div>

  <ReportHeader
    v-if="isKanbanEnabled"
    :header-title="t('KANBAN.LOSS_REPORT.TITLE')"
  />

  <div v-if="isKanbanEnabled" class="flex flex-col gap-6">
    <!-- Filters (CSAT pattern) -->
    <div class="flex flex-col flex-wrap w-full gap-3 md:flex-row">
      <SelectMenu
        :model-value="selectedBoardId || ''"
        :options="boardOptions"
        :label="selectedBoardLabel"
        sub-menu-position="bottom"
        max-width="max-w-56"
        @update:model-value="onBoardChange"
      />

      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />

      <div
        class="flex flex-col flex-wrap items-start gap-2 md:items-center md:flex-nowrap md:flex-row"
      >
        <div v-if="hasActiveFilters" class="flex flex-wrap gap-2 md:flex-nowrap">
          <ActiveFilterChip
            v-for="filter in activeFilters"
            v-bind="filter"
            :key="filter.type"
            :placeholder="
              t(
                `CSAT_REPORTS.FILTERS.INPUT_PLACEHOLDER.${filter.type.toUpperCase()}`
              )
            "
            :active-filter-type="activeFilterType"
            :show-menu="showSubDropdownMenu"
            enable-search
            @toggle-dropdown="openActiveFilterDropdown"
            @close-dropdown="() => { showSubDropdownMenu = false; activeFilterType = ''; }"
            @add-filter="addFilter"
            @remove-filter="removeFilter"
          />
        </div>

        <div
          v-if="hasActiveFilters && !isAllFilterSelected"
          class="w-full h-px border md:w-px md:h-5 border-n-weak"
        />

        <div class="flex items-center gap-2">
          <AddFilterChip
            v-if="!isAllFilterSelected"
            placeholder-i18n-key="CSAT_REPORTS.FILTERS.INPUT_PLACEHOLDER"
            :name="t('CSAT_REPORTS.FILTERS.ADD_FILTER')"
            :menu-option="filterListMenuItems"
            :show-menu="showDropdownMenu"
            :empty-state-message="t('CSAT_REPORTS.FILTERS.NO_FILTER')"
            @toggle-dropdown="showDropdown"
            @close-dropdown="() => { showDropdownMenu = false; }"
            @add-filter="addFilter"
          />

          <div v-if="hasActiveFilters" class="w-px h-5 border border-n-weak" />

          <FilterButton
            v-if="hasActiveFilters"
            :button-text="t('CSAT_REPORTS.FILTERS.CLEAR_ALL')"
            @click="clearAllFilters"
          />
        </div>
      </div>
    </div>

    <!-- Summary cards (CsatMetrics pattern) -->
    <div
      class="flex sm:flex-row flex-col w-full gap-4 sm:gap-14 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <CsatMetricCard
        :label="t('KANBAN.LOSS_REPORT.SUMMARY.TOTAL_LOSSES')"
        :tooltip="t('KANBAN.LOSS_REPORT.SUMMARY.TOTAL_LOSSES_TOOLTIP')"
        :value="summary.total_losses.toLocaleString()"
        :is-loading="isLoading"
      />
      <div class="w-full sm:w-px bg-n-strong" />
      <CsatMetricCard
        :label="t('KANBAN.LOSS_REPORT.SUMMARY.TOTAL_VALUE')"
        :tooltip="t('KANBAN.LOSS_REPORT.SUMMARY.TOTAL_VALUE_TOOLTIP')"
        :value="formatCurrency(summary.total_value)"
        :is-loading="isLoading"
      />
    </div>

    <!-- Table card (SummaryReports pattern) -->
    <div
      class="relative flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <!-- Empty state -->
      <div
        v-if="!isLoading && rows.length === 0"
        class="flex flex-col items-center justify-center py-16"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('KANBAN.LOSS_REPORT.EMPTY') }}
        </span>
      </div>

      <!-- Data table -->
      <table v-else class="w-full text-sm text-n-slate-12">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="px-4 py-3 text-start font-medium text-n-slate-11 whitespace-nowrap"
            >
              {{ t('KANBAN.LOSS_REPORT.TABLE.REASON') }}
            </th>
            <th
              class="px-4 py-3 text-end font-medium text-n-slate-11 whitespace-nowrap"
            >
              {{ t('KANBAN.LOSS_REPORT.TABLE.COUNT') }}
            </th>
            <th
              class="px-4 py-3 text-end font-medium text-n-slate-11 whitespace-nowrap"
            >
              {{ t('KANBAN.LOSS_REPORT.TABLE.VALUE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="group in grouped"
            :key="group.teamId ?? '__none__'"
          >
            <!-- Team header row -->
            <tr class="bg-n-slate-2 border-t border-n-strong first:border-t-0">
              <td colspan="3" class="px-4 py-2 font-semibold text-n-slate-12">
                {{ group.teamName || t('KANBAN.LOSS_REPORT.TABLE.NO_TEAM') }}
              </td>
            </tr>
            <!-- Agent groups -->
            <template
              v-for="agentGroup in group.agents"
              :key="agentGroup.agentId ?? '__none__'"
            >
              <!-- Agent header row -->
              <tr class="bg-n-slate-1 border-t border-n-weak">
                <td colspan="3" class="px-4 py-2 pl-8 font-medium text-n-slate-11">
                  <span v-if="agentGroup.agentName">
                    {{ agentGroup.agentName }}
                  </span>
                  <span v-else class="text-n-slate-9 italic">{{
                    t('KANBAN.LOSS_REPORT.TABLE.NO_AGENT')
                  }}</span>
                </td>
              </tr>
              <!-- Reason rows -->
              <tr
                v-for="reason in agentGroup.reasons"
                :key="reason.reasonId ?? '__noreason__'"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-slate-1 transition-colors"
              >
                <td class="px-4 py-3 pl-12 text-n-slate-11">
                  {{ reason.reasonName || '—' }}
                </td>
                <td class="px-4 py-3 text-end tabular-nums font-medium">
                  {{ reason.lossCount }}
                </td>
                <td class="px-4 py-3 text-end tabular-nums text-n-slate-11">
                  {{ formatCurrency(reason.totalValue) }}
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>

      <!-- Loading overlay (SummaryReports pattern) -->
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        leave-active-class="transition-opacity duration-200 ease-in"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 flex justify-center pt-[12.5rem] bg-n-solid-1/70 rounded-xl pointer-events-none"
        >
          <Spinner :size="32" class="text-n-brand" />
        </div>
      </Transition>
    </div>
  </div>
</template>
